import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import { Marker } from 'react-leaflet'
import useDirectory from 'hooks/useDirectory'
import { LatLngTuple } from 'leaflet'
import { Floor } from 'models/Building'
import useSWR from 'swr'
import { FloorDetail, Point } from 'types/building'

import Button from 'components/atoms/Button'
import Spinner from 'components/atoms/Spinner'
import OpenMiniMapIcon from 'components/icons/OpenMiniMapIcon'
import TourViewerOpenIcon from 'components/icons/TourViewerOpenIcon'

import useFloorMap from '../../hook/Leflet/useFloorMap'
import useRadarPointMap from '../../hook/Leflet/useRadarPointMap'
import { SelectOption } from '../type'

import FloorMapView from './FloorMapView'
import { PointStyle } from './PointStyle'
import SelectFloor from './SelectFloor'
import ZoomControl from './ZoomControl'

/* eslint-disable react/require-default-props */
interface TourViewerFloorMapProps {
  floors: Floor[] | undefined
  setSelectedPointID: Dispatch<SetStateAction<string>>
  onMapLoaded: (floorId?: number | null) => void
  shouldHideUI?: boolean
  points: Point[]
}

export default function TourViewerFloorMap({
  floors,
  setSelectedPointID,
  onMapLoaded,
  shouldHideUI = false,
  points
}: TourViewerFloorMapProps) {
  const { directory } = useDirectory()
  const isCommunity = directory === 'community'
  const apiPrefix = isCommunity ? '/v1/tour' : '/v1'
  const [isMediumScreen, setIsMediumScreen] = useState(false)
  // Prevent 60fps React re-renders which cause severe jitter and lag.
  // We use a ref to store the latest angle so an unrelated render doesn't reset the marker.
  const latestAngleRef = useRef(0)
  const [radarReady, setRadarReady] = useState(false)
  const [hideSelectFloor, setHideSelectFloor] = useState<boolean>(false)
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false)
  const [isMapBackgroundHovered, setIsMapBackgroundHovered] = useState(false)
  // Use the floor map hook for map management
  const {
    mapContainerRef,
    isZoomOutMap,
    isMapLoading,
    showMap,
    imgBounds,
    getMapSizeInlineStyle,
    handleZoomToggle,
    handleZoomIn,
    handleZoomOut,
    handleZoomToPoint,
    handleResetZoom,
    resetMap,
    getFloorSelectInlineStyle,
    handleSelectFloor,
    currentViewer,
    handleMarkerClick,
    sync360ViewToArc,
    povMarkerRef,
    floorDetail,
    setFloorDetail,
    zoomValue,
    setCurrentViewer
  } = useFloorMap({
    initialZoomOut: false,
    setSelectedPointID,
    points
  })

  // Use the radar point map hook
  const {
    getRotatedMarkerIcon,
    mapEventHandler,
    radarEventHandler,
    radarMaker: radarMakerData
  } = useRadarPointMap({
    points,
    currentViewer,
    isZoomOutMap,
    handleMarkerClick,
    povMarkerRef
  })

  // Fetch floor detail
  const { isValidating: isValidatingFloor } = useSWR<FloorDetail>(
    currentViewer?.selectedFloor
      ? {
          url: `${apiPrefix}/floors/${currentViewer?.selectedFloor}`
        }
      : null,
    {
      onSuccess: (data) => {
        setFloorDetail(data)
        resetMap() // Reset map when floor changes
      }
    }
  )

  // Notify parent when map for the current floor finished loading
  useEffect(() => {
    if (!isMapLoading && !isValidatingFloor && floorDetail) {
      onMapLoaded?.(currentViewer?.selectedFloor)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMapLoading, isValidatingFloor, floorDetail, onMapLoaded])

  // Prepare floor options for dropdown
  const floorOptions: SelectOption[] =
    floors?.map((floor) => ({
      value: floor.id,
      label: (
        <span className="text-[#fff] text-[13px] font-bold">{floor.name}</span>
      )
    })) || []

  // Enhanced point style
  const getEnhancedPointStyle = useCallback(
    (
      point: any,
      selectedPointId: string | undefined,
      isZoomed: boolean,
      showNumbers: boolean
    ) => {
      return PointStyle(point, selectedPointId, isZoomed, showNumbers)
    },
    []
  )

  // Track click state
  const clickTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const [clickCount, setClickCount] = useState(0)

  const isDraggingRef = useRef(false)
  const mouseDownPositionRef = useRef<{ x: number; y: number } | null>(null)

  const handleMouseDown = (e: React.MouseEvent) => {
    mouseDownPositionRef.current = { x: e.clientX, y: e.clientY }
    isDraggingRef.current = false
  }

  const handleMouseMove = (e: React.MouseEvent) => {
    if (mouseDownPositionRef.current) {
      const deltaX = Math.abs(e.clientX - mouseDownPositionRef.current.x)
      const deltaY = Math.abs(e.clientY - mouseDownPositionRef.current.y)

      if (deltaX > 5 || deltaY > 5) {
        isDraggingRef.current = true
      }
    }
  }
  const handleMouseUp = () => {
    mouseDownPositionRef.current = null
  }

  const handleClick = (e: React.MouseEvent) => {
    if (isDraggingRef.current) {
      isDraggingRef.current = false
      return
    }
    e.preventDefault()
    setClickCount((prev) => prev + 1)

    if (clickTimeoutRef.current) {
      clearTimeout(clickTimeoutRef.current)
    }

    clickTimeoutRef.current = setTimeout(() => {
      if (clickCount === 0) {
        handleZoomToggle()
      }
      setClickCount(0)
    }, 300)
  }

  const handleDoubleClick = (e: React.MouseEvent) => {
    e.preventDefault()
    if (clickTimeoutRef.current) {
      clearTimeout(clickTimeoutRef.current)
    }
    setClickCount(0)
  }

  // Clean up timeout on component unmount
  useEffect(() => {
    return () => {
      if (clickTimeoutRef.current) {
        clearTimeout(clickTimeoutRef.current)
      }
    }
  }, [])

  // Check if screen is medium or larger
  useEffect(() => {
    const checkScreenSize = () => {
      setIsMediumScreen(window.innerWidth >= 768)
    }

    checkScreenSize()
    window.addEventListener('resize', checkScreenSize)

    return () => {
      window.removeEventListener('resize', checkScreenSize)
    }
  }, [])

  // Store last point and click state to reset radar immediately during render, preventing 1-frame flashes
  const prevPointRef = useRef(currentViewer?.selectedPoint)
  const isTransitioningPhase = useRef(false)
  let justStarted = false

  if (prevPointRef.current !== currentViewer?.selectedPoint) {
    isTransitioningPhase.current = true
    justStarted = true
    setRadarReady(false)
    prevPointRef.current = currentViewer?.selectedPoint

    // Reset angle to default when switching points to ensure radar points correctly even before Krpano signals
    const nextPoint = points.find((p) => p.id === currentViewer?.selectedPoint)
    if (nextPoint) {
      latestAngleRef.current =
        (nextPoint.initView?.h || 0) + (nextPoint.initView?.radarHGap || 0)
    }
  }

  const prevIsClickToDirectionRef = useRef(currentViewer?.isClickToDirection)
  if (currentViewer?.isClickToDirection && !prevIsClickToDirectionRef.current) {
    isTransitioningPhase.current = true
    justStarted = true
    setRadarReady(false)
  }
  prevIsClickToDirectionRef.current = currentViewer?.isClickToDirection

  if (radarReady && !justStarted) {
    isTransitioningPhase.current = false
  }

  // Generate radar marker component
  const radarMaker = useMemo(() => {
    if (!radarMakerData || !radarReady || isTransitioningPhase.current)
      return null

    const position: LatLngTuple = [
      radarMakerData?.position[0],
      radarMakerData?.position[1]
    ]

    const isExplicitSelection =
      currentViewer.isSelectPointInLeftMenu ||
      currentViewer.isClickFromTourCarousel ||
      currentViewer.isSelectPoint

    const radarRotate = isExplicitSelection
      ? radarMakerData?.rotate
      : latestAngleRef.current

    return (
      <Marker
        position={position}
        ref={povMarkerRef}
        zIndexOffset={radarMakerData?.zIndexOffset}
        icon={getRotatedMarkerIcon(radarRotate, isZoomOutMap)}
        draggable={false}
        eventHandlers={radarEventHandler()}
      />
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    radarReady,
    radarMakerData,
    povMarkerRef,
    isZoomOutMap,
    getRotatedMarkerIcon,
    radarEventHandler
  ])

  // Wrap in useEffect to avoid registering multiple global callbacks.
  useEffect(() => {
    sync360ViewToArc((rotationAngle: number) => {
      latestAngleRef.current = rotationAngle
      setCurrentViewer((prev) => {
        if (
          !prev.isSelectPoint &&
          !prev.isSelectPointInLeftMenu &&
          !prev.isClickFromTourCarousel
        )
          return prev
        return {
          ...prev,
          isSelectPoint: false,
          isSelectPointInLeftMenu: false,
          isClickFromTourCarousel: false
        }
      })
      return getRotatedMarkerIcon(rotationAngle, isZoomOutMap)
    })
  }, [sync360ViewToArc, getRotatedMarkerIcon, isZoomOutMap, setCurrentViewer])

  // Effect 2: Show radar only after Krpano signals the scene is fully settled.
  // Krpano uses a 1-second BLEND animation. 'imageIsLoading' becomes false
  // after ~500ms, which is halfway through the animation. Added a 600ms timeout
  // to ensure the visual transition completes entirely before the radar appears.
  useEffect(() => {
    if (
      currentViewer?.selectedPoint &&
      currentViewer?.pointDetail?.id === currentViewer?.selectedPoint &&
      !currentViewer?.imageIsLoading
    ) {
      const timeout = setTimeout(() => {
        setRadarReady(true)
      }, 600)
      return () => clearTimeout(timeout)
    }
  }, [
    currentViewer?.imageIsLoading,
    currentViewer?.selectedPoint,
    currentViewer?.pointDetail?.id
  ])

  // Compute map area class to avoid nested ternary
  const getMapAreaClass = () => {
    if (shouldHideUI || hideSelectFloor)
      return 'opacity-0 translate-y-2 pointer-events-none'
    return 'opacity-100 translate-y-0'
  }
  return (
    <div
      className={`absolute top-0 right-0 z-[99] tour-view-map-mini ${
        isDropdownOpen ? 'overflow-visible' : 'overflow-hidden'
      } ${shouldHideUI ? 'opacity-0 pointer-events-none' : ''}`}
    >
      <div id="miniMap" className="flex justify-end overflow-visible">
        <div
          className={`floor-controls-row ${hideSelectFloor ? 'is-hidden' : ''}`}
        >
          <Button
            id="toggleMiniMapBtn"
            sizing="w-[19px] h-[40px]"
            className="bg-[#074947] flex justify-center items-center !text-white !rounded-none"
            onClick={() => setHideSelectFloor((prev) => !prev)}
          >
            <TourViewerOpenIcon
              className={` ${hideSelectFloor ? 'rotate-0' : '-rotate-180'}`}
            />
          </Button>

          <div id="selectFloor" className="select-floor-nav">
            <SelectFloor
              style={getFloorSelectInlineStyle()}
              options={floorOptions}
              value={currentViewer?.selectedFloor}
              onSelect={handleSelectFloor}
              onOpenStateChange={setIsDropdownOpen}
            />
          </div>
        </div>

        {(hideSelectFloor || shouldHideUI) && (
          <div
            id="openMiniMapIcon"
            className={`w-[40px] h-[40px] bg-[#074947]/50 backdrop-blur-xl flex justify-center items-center !rounded-none ${
              shouldHideUI ? '' : 'cursor-pointer'
            }`}
            onClick={() => {
              if (!shouldHideUI) {
                setHideSelectFloor(false)
              }
            }}
          >
            <OpenMiniMapIcon />
          </div>
        )}
      </div>
      {/* Map area */}
      {floorDetail?.mapFile?.s3Path && (
        <div
          className={`relative w-full transition-all duration-500 ease-in-out grid ${
            hideSelectFloor || shouldHideUI
              ? 'grid-rows-[0fr]'
              : 'grid-rows-[1fr]'
          } ${getMapAreaClass()}`}
        >
          <div className="overflow-hidden min-h-0 w-full">
            <Spinner
              spinning={!shouldHideUI && (isValidatingFloor || isMapLoading)}
            >
              <div className="flex flex-col items-end">
                <div
                  className={`relative bg-white cursor-pointer transition-all duration-500 ease-in-out ${
                    showMap
                      ? 'opacity-100 scale-100'
                      : 'opacity-0 scale-95 pointer-events-none'
                  } ${shouldHideUI ? 'opacity-0 pointer-events-none' : ''}`}
                  style={{
                    ...getMapSizeInlineStyle()
                  }}
                  onClick={handleClick}
                  onDoubleClick={handleDoubleClick}
                  onMouseEnter={() => setIsMapBackgroundHovered(true)}
                  onMouseLeave={() => setIsMapBackgroundHovered(false)}
                  onMouseDown={handleMouseDown}
                  onMouseMove={handleMouseMove}
                  onMouseUp={handleMouseUp}
                >
                  <FloorMapView
                    mapContainerRef={mapContainerRef}
                    floorDetail={floorDetail}
                    points={points}
                    imgBounds={imgBounds}
                    currentViewer={currentViewer}
                    isZoomOutMap={isZoomOutMap}
                    isMediumScreen={isMediumScreen}
                    handleMarkerClick={handleMarkerClick}
                    radarMaker={radarMaker}
                    getEnhancedPointStyle={getEnhancedPointStyle}
                    mapEventHandler={mapEventHandler}
                    showNumbers={false}
                    handleZoomToggle={handleZoomToggle}
                    setIsMapBackgroundHovered={setIsMapBackgroundHovered}
                  />
                  {/* Loading overlay */}
                  {isMapLoading && !shouldHideUI && (
                    <div className="absolute inset-0 flex items-center justify-center z-10 bg-white bg-opacity-50 transition-opacity duration-500">
                      <div className="animate-pulse flex flex-col items-center">
                        <div
                          className={`${
                            isZoomOutMap ? 'w-12 h-12' : 'w-2 h-2'
                          } border-4 border-t-4 border-gray-200 rounded-full animate-spin mb-2 transition-all duration-500`}
                          style={{ borderTopColor: '#5F9EA0' }}
                        />
                        <div className="text-sm text-gray-600 transition-opacity duration-500">
                          読み込み中...
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <div className="relative w-full flex items-center justify-end gap-2">
                  {isMapBackgroundHovered && !shouldHideUI && isZoomOutMap && (
                    <div className="text-white text-[11px] whitespace-nowrap rounded px-2 py-1">
                      クリックで拡大縮小
                    </div>
                  )}
                  <div className="flex flex-col items-end">
                    <div
                      className={`transition-all duration-500 ease-in-out ${
                        !shouldHideUI
                          ? 'opacity-100 translate-y-0'
                          : 'opacity-0 translate-y-2'
                      }`}
                    >
                      <ZoomControl
                        isZoomOutMap={isZoomOutMap}
                        zoomIn={handleZoomIn}
                        zoomOut={handleZoomOut}
                        zoomPoint={(e?: React.MouseEvent) => {
                          if (e) {
                            e.preventDefault()
                            e.stopPropagation()
                          }
                          if (mapContainerRef?.current?.dragging) {
                            mapContainerRef.current.dragging.disable()
                          }
                          if (currentViewer.selectedPoint) {
                            const point = points.find(
                              (p) => p.id === currentViewer.selectedPoint
                            )
                            if (point) {
                              handleZoomToPoint(point.x, point.y)
                            }
                          }
                        }}
                        zoomValue={zoomValue}
                        handleResetZoom={handleResetZoom}
                      />
                    </div>
                    {isMapBackgroundHovered &&
                      !shouldHideUI &&
                      !isZoomOutMap && (
                        <div className="text-white text-[11px] whitespace-nowrap rounded px-2 py-1">
                          クリックで拡大縮小
                        </div>
                      )}
                  </div>
                </div>
              </div>
            </Spinner>
          </div>
        </div>
      )}
    </div>
  )
}
